import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useVorgaenge } from "@/hooks/useVorgaenge";
import {
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  X,
} from "lucide-react";

/**
 * VorgangsTray
 *
 * Live-Chip in der Sidebar, der KI-Generierungen sichtbar hält, egal auf
 * welcher Seite der Unternehmer arbeitet. Wird in `UnternehmerLayout`
 * gemountet, sobald `VorgaengeProvider` den Outlet umschließt.
 *
 * Drei Zustände pro Eintrag:
 * - generating → blauer Spinner + verstrichene Zeit + Abbrechen-X
 * - ready     → grüner Check + „Fertig" + Anzeigen-Button (Backend hält
 *               diesen Status genau 30 s sichtbar, danach blendet er aus)
 * - error     → rotes Warn-Icon + Fehlermeldung
 */
export default function VorgangsTray({ variant = "desktop" }) {
  const navigate = useNavigate();
  const { jobs, cancel } = useVorgaenge();
  const [open, setOpen] = useState(false);
  // Tick-basierte Uhrzeit als State, damit Date.now() nicht im Render-Body
  // landet (React-19-Purity-Regel). Wird im 1-s-Takt aktualisiert, sodass der
  // verstrichene Sekundenzähler im Popover flüssig läuft.
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!jobs || jobs.length === 0) {
    return null;
  }

  const activeCount = jobs.filter((j) => j.status === "generating").length;
  const readyCount = jobs.filter((j) => j.status === "ready").length;
  const errorCount = jobs.filter((j) => j.status === "error").length;

  // Chip-Farbe richtet sich nach Schwerpunkt: rot > grün > blau.
  let chipClass =
    "bg-sky-100 text-sky-800 hover:bg-sky-200 border-sky-200";
  let ChipIcon = Loader2;
  let chipLabel =
    activeCount > 0
      ? activeCount === 1
        ? "1 Vorgang läuft"
        : `${activeCount} Vorgänge laufen`
      : readyCount > 0
        ? readyCount === 1
          ? "1 fertig"
          : `${readyCount} fertig`
        : `${errorCount} Fehler`;
  let iconAnimate = activeCount > 0;
  if (errorCount > 0 && activeCount === 0) {
    chipClass = "bg-rose-100 text-rose-800 hover:bg-rose-200 border-rose-200";
    ChipIcon = AlertTriangle;
    iconAnimate = false;
  } else if (activeCount === 0 && readyCount > 0) {
    chipClass =
      "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border-emerald-200";
    ChipIcon = CheckCircle2;
    iconAnimate = false;
  }

  const elapsedFor = (job) => {
    if (!job.started_at) return job.elapsed_seconds || 0;
    const diff = Math.max(
      0,
      Math.floor((nowMs - new Date(job.started_at).getTime()) / 1000)
    );
    return diff;
  };

  const remainingFor = (job) => {
    const ref = job.finished_at || job.started_at;
    if (!ref) return 30;
    return Math.max(
      0,
      30 - Math.floor((nowMs - new Date(ref).getTime()) / 1000)
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`vorgangs-tray-chip-${variant}`}
          className={`mt-3 w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-xs font-medium transition-colors ${chipClass}`}
        >
          <span className="inline-flex items-center gap-2 min-w-0">
            <ChipIcon
              className={`w-3.5 h-3.5 shrink-0 ${iconAnimate ? "animate-spin" : ""}`}
            />
            <span className="truncate">{chipLabel}</span>
          </span>
          <span className="text-[10px] uppercase tracking-wider opacity-70">
            Vorgänge
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="right"
        sideOffset={12}
        className="w-80 p-0"
        data-testid={`vorgangs-tray-popover-${variant}`}
      >
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="font-heading font-semibold text-slate-900 text-sm">
            Aktive Vorgänge
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            KI-Generierungen laufen im Hintergrund – Sie können weiterarbeiten.
          </div>
        </div>
        <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
          {jobs.map((job) => {
            const elapsed = elapsedFor(job);
            return (
              <li
                key={job.id}
                className="px-4 py-3 flex items-start gap-3"
                data-testid={`vorgang-row-${job.id}`}
              >
                <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5">
                  {job.status === "generating" && (
                    <Loader2 className="w-4 h-4 animate-spin text-sky-600" />
                  )}
                  {job.status === "ready" && (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  )}
                  {job.status === "error" && (
                    <AlertTriangle className="w-5 h-5 text-rose-600" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className="text-sm font-medium text-slate-900 truncate"
                    title={job.title}
                  >
                    {job.title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {job.status === "generating" && (
                      <>Wird erstellt … {elapsed} s</>
                    )}
                    {job.status === "ready" && (
                      <>Fertig – verschwindet in {remainingFor(job)} s</>
                    )}
                    {job.status === "error" && (
                      <span className="text-rose-700">
                        {job.error_message || "Fehlgeschlagen."}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {job.status === "ready" && job.is_world && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setOpen(false);
                        navigate(`/unternehmer/welt/${job.id}`);
                      }}
                      data-testid={`vorgang-open-${job.id}`}
                      className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                    >
                      Anzeigen
                    </Button>
                  )}
                  {job.status === "generating" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancel(job.id)}
                      data-testid={`vorgang-cancel-${job.id}`}
                      className="h-7 w-7 p-0 text-slate-500 hover:text-rose-700 hover:bg-rose-50"
                      aria-label="Abbrechen"
                      title="Generierung abbrechen"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-1.5 text-[11px] text-slate-500">
          {activeCount > 0 ? (
            <>
              <Sparkles className="w-3 h-3" />
              KI-Generierung läuft auf unseren Servern – kein Tab-Zwang.
            </>
          ) : (
            <>
              <CheckCircle2 className="w-3 h-3" />
              Alle Vorgänge abgeschlossen.
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
